import React, { useState, useEffect, useMemo } from 'react';
import { getProjectInspections, getAdditionalFieldsForTable } from 'utils/InspectionsUtils';
import { InspectionView } from 'classes';
import { useAppContext } from 'hooks/useAppContext';
import SkeletonGrid from 'components/Shared/SkeletonGrid';
import MaterialTable from 'material-table';
import withAppStylesGrid from 'HOCs/withAppStyles';
import { useTheme, Grid, Menu } from '@material-ui/core';
import { Flare, ListAlt, Create, Scanner,
  AccessTime, NewReleases, Chat, LocalShipping, ErrorOutline } from '@material-ui/icons';
import ReviewDrawer from 'components/Inspections/ReviewDrawer';
import MenuIcon from '@material-ui/icons/Menu';
import AuthCheck from 'components/Shared/AuthCheck';
import SideMenuItem from 'components/Shared/SideMenuItem';
import { useAuthContext } from 'hooks/useAuthContext';
import { useCanDoAction } from 'hooks/useCanDoAction';
import { checkHideShow, getNestedRoutes } from 'utils/uxUtils';
import { useMenuStyles } from 'components/Menu';
import CommentsDisplay from './CommentsDisplay';

// Memoized Grid component that only recreates when selectedProject changes
const MemoGrid = (props) => {
  const { selectedProject } = useAppContext();
  const theme = useTheme();

  // Memoize the Grid component to prevent recreating on every render
  const StyledGrid = useMemo(() => {
    return withAppStylesGrid(MaterialTable, {
      projectID: selectedProject,
      getRecords: getProjectInspections,
      theme,
      defaultFilters: { projectId: selectedProject, inspectionNumber: props.inspectionNumber },
      exportFilter: true
    });
  }, [selectedProject, theme, props.inspectionNumber]);

  return <StyledGrid {...props} />
};

// Fixed: Allow re-render when columns prop changes
const WrappedGrid = React.memo(MemoGrid, (prevProps, nextProps) => {
  // Return true if props are equal (don't re-render)
  // Return false if props changed (re-render)
  return prevProps.columns === nextProps.columns;
});

export default function InspectionsGrid({ inspectionNumber }) {
  const baseColumns = InspectionView.columns;
  const [columns, setColumns] = useState(baseColumns);
  const classes = useMenuStyles();
  const { role } = useAuthContext();
  const { selectedProject, currentModule, selectedProjectData } = useAppContext(); //Getting the App Context
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(undefined);
  // ref to the underlying material-table so we can refresh its data in place
  // (via onQueryChange) without remounting and losing the user's sort/filter/page.
  const tableRef = React.useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [clickedInspection, setClickedInspection] = useState(null);
  const title = "Inspections";
  const theme = useTheme();

  // Fetch Additional Fields configuration and filter columns
  useEffect(() => {
    console.log('=== Inspections Grid Column Filter Effect Running ===');
    console.log('selectedProject:', selectedProject);
    console.log('selectedProjectData:', selectedProjectData);

    // Only run when both values are available to prevent multiple renders
    if (!selectedProject || !selectedProjectData) {
      console.log('Waiting for selectedProject and selectedProjectData to be available...');
      return;
    }

    getAdditionalFieldsForTable(selectedProject, "INSPECTION")
      .then(additionalFields => {
        console.log('Additional Fields configuration for INSPECTION:', additionalFields);
        console.log('Section Settings:', selectedProjectData.sectionSetting);

        // Map column titles to module names for section settings check
        const columnToModuleMap = {
          "Field Test": "Tests",
          "Samples": "Samples",
          "Checklist": "Checklist"
        };

        // Filter columns based on Additional Fields configuration AND section settings
        const filteredColumns = baseColumns.filter(column => {
          console.log(`\nChecking column: "${column.title}"`);

          // 1. Check Additional Fields configuration
          const fieldConfig = additionalFields[column.title];
          console.log(`  - Additional Fields config for "${column.title}":`, fieldConfig);
          if (fieldConfig !== undefined && fieldConfig === false) {
            console.log(`  ❌ Excluding column "${column.title}" based on Additional Fields config`);
            return false;
          }

          // 2. Check section settings for sub-module columns
          const moduleName = columnToModuleMap[column.title];
          if (moduleName) {
            console.log(`  - Mapped to module: "${moduleName}"`);
            const isModuleVisible = checkHideShow(moduleName, selectedProjectData.sectionSetting);
            console.log(`  - Module "${moduleName}" visible:`, isModuleVisible);
            if (!isModuleVisible) {
              console.log(`  ❌ Excluding column "${column.title}" because "${moduleName}" module is hidden in section settings`);
              return false;
            }
          }

          console.log(`  ✅ Including column "${column.title}"`);
          return true;
        });

        console.log(`\n=== SUMMARY: Filtered ${baseColumns.length} columns down to ${filteredColumns.length} columns ===`);
        console.log('Included columns:', filteredColumns.map(c => c.title));

        // Only update state if columns actually changed
        setColumns(prevColumns => {
          const columnsChanged = JSON.stringify(prevColumns.map(c => c.field)) !== JSON.stringify(filteredColumns.map(c => c.field));
          if (columnsChanged) {
            console.log('Columns changed, updating state');
            return filteredColumns;
          } else {
            console.log('Columns unchanged, skipping state update');
            return prevColumns;
          }
        });
      })
      .catch(error => {
        console.error('Error fetching Additional Fields:', error);
        // On error, use all columns
        setColumns(baseColumns);
      });
  }, [selectedProject, selectedProjectData]);

  const handleClick = (event, rowData) => {
    setClickedInspection(rowData);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setClickedInspection(null);
  };

  const RenderMenu = React.forwardRef((props, ref) => (
    clickedInspection && <Menu
      id="simple-menu"
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleClose}
      ref={ref}
    >
      {
        checkHideShow("Samples", selectedProjectData.sectionSetting) &&
        <AuthCheck role={role} scope="Samples" action="read">
          <SideMenuItem key="samples" moduleName="Samples" handleClick={handleClick} icon={Flare} className={classes.nestedItem} route={`/projects/${selectedProject}/inspections/${clickedInspection.InspectionNumber}/samples`} />
        </AuthCheck>
      }
      {
        checkHideShow("Tests", selectedProjectData.sectionSetting) &&
        <AuthCheck role={role} scope="Tests" action="read">
          <SideMenuItem key="Field Tests" moduleName="Field Tests" route={getNestedRoutes(currentModule, clickedInspection.InspectionNumber, selectedProject, "fieldtests")} handleClick={handleClick} icon={ListAlt} className={classes.nestedItem} />
        </AuthCheck>
      }
      {
        checkHideShow("Equipment", selectedProjectData.sectionSetting) &&
        <AuthCheck role={role} scope="Inspections" action="read">
          <SideMenuItem key="Equipment" moduleName="Equipment" route={getNestedRoutes(currentModule, clickedInspection.InspectionNumber, selectedProject, "equipment")} handleClick={handleClick} icon={Scanner} className={classes.nestedItem} />
        </AuthCheck>
      }
      {
        checkHideShow("Delays", selectedProjectData.sectionSetting) &&
        <AuthCheck role={role} scope="Inspections" action="read">
          <SideMenuItem key="Delays" moduleName="Delays" route={getNestedRoutes(currentModule, clickedInspection.InspectionNumber, selectedProject, "delays")} handleClick={handleClick} icon={AccessTime} className={classes.nestedItem} />
        </AuthCheck>
      }
      {
        checkHideShow("Accidents", selectedProjectData.sectionSetting) &&
        <AuthCheck role={role} scope="Inspections" action="read">
          <SideMenuItem key="Accidents" moduleName="Accidents" route={getNestedRoutes(currentModule, clickedInspection.InspectionNumber, selectedProject, "accidents")} handleClick={handleClick} icon={NewReleases} className={classes.nestedItem} />
        </AuthCheck>
      }
      {
        checkHideShow("Comments", selectedProjectData.sectionSetting) &&
        <AuthCheck role={role} scope="Comments" action="read">
          <SideMenuItem key="Comments" moduleName="Comments" route={getNestedRoutes(currentModule, clickedInspection.InspectionNumber, selectedProject, "comments")} handleClick={handleClick} icon={Chat} className={classes.nestedItem} />
        </AuthCheck>
      }
      {
        checkHideShow("Deliveries", selectedProjectData.sectionSetting) &&
        <AuthCheck role={role} scope="Inspections" action="read">
          <SideMenuItem key="Deliveries" moduleName="Deliveries" route={`/projects/${selectedProject}/inspections/${clickedInspection.InspectionNumber}/deliveries`} handleClick={handleClick} icon={LocalShipping} className={classes.nestedItem} />
        </AuthCheck>
      }
      {
        checkHideShow("Issues", selectedProjectData.sectionSetting) &&
        <AuthCheck role={role} scope="Issues" action="read">
          <SideMenuItem key="Issues" moduleName="Issues" route={`/projects/${selectedProject}/inspections/${clickedInspection.InspectionNumber}/issues`} handleClick={handleClick} icon={ErrorOutline} className={classes.nestedItem} />
        </AuthCheck>
      }
    </Menu>
  ))

  const canView = useCanDoAction('Inspections', 'read');

  const actions = React.useMemo(() => {
    const _actions = [
      {
        icon: () => <MenuIcon />,
        tooltip: "View inspection's related items",
        onClick: handleClick
      },
    ];

    if (canView) {
      _actions.push({
        icon: () => <Create />,
        tooltip: 'Approve/Deny Inspection',
        onClick: (event, rowData) => {
          setSelectedRowData(rowData);
          setDrawerOpen(true);
        },
      })
    };

    return _actions;
  }, [canView]);

  return (
    selectedProject && theme ?
      <>
        <Grid container>
          <Grid item xs={drawerOpen ? 9 : 12}>
            <WrappedGrid
              tableRef={tableRef}
              columns={columns}
              title={title}
              options={{
                pageSize: 15,
                pageSizeOptions: [15, 20, 25],
                debounceInterval: 500
              }}
              actions={actions}
              detailPanel={rowData => {
                return (
                  <CommentsDisplay comment={rowData} ></CommentsDisplay>
                )
              }}
            />
          </Grid>
          {
            drawerOpen &&
            <ReviewDrawer open={drawerOpen} close={() => setDrawerOpen(false)} rowData={selectedRowData} refreshGrid={() => { if (tableRef.current) tableRef.current.onQueryChange(); }} />
          }
        </Grid>
        <RenderMenu />
      </>
      : <SkeletonGrid title={title} />
  )
}